"use client";

import { StarIcon, XIcon } from "@/assets/icons";
import {
  type ProductReviewItem,
  type ProductReviewSort,
  type ProductReviewStats,
} from "@/lib/reviews-api";
import { useTranslations } from "next-intl";
import { useEffect, useMemo } from "react";
import Pagination from "./Pagination";

import { formatRelativeDate } from "@/app/utils/formatRelativeDate";

interface ReviewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  hasProductContext: boolean;
  isLoading: boolean;
  error: string;
  reviews: ProductReviewItem[];
  stats: ProductReviewStats;
  total: number;
  page: number;
  pageSize: number;
  sort: ProductReviewSort;
  onPageChange: (page: number) => void;
  onSortChange: (sort: ProductReviewSort) => void;
  onRetry: () => void;
}

const ReviewsModal = ({
  isOpen,
  onClose,
  productName,
  hasProductContext,
  isLoading,
  error,
  reviews,
  stats,
  total,
  page,
  pageSize,
  sort,
  onPageChange,
  onSortChange,
  onRetry,
}: ReviewsModalProps) => {
  const t = useTranslations("ReviewsModal");

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const totalReviews = stats.total || total;

  const ratingRows = useMemo(() => {
    const ratingBreakdown = stats.breakdown ?? { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    return ([5, 4, 3, 2, 1] as const).map((rating) => {
      const count = ratingBreakdown[rating] ?? 0;
      const widthPercent =
        totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
      return { rating, count, widthPercent };
    });
  }, [stats.breakdown, totalReviews]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100000] bg-dark/70 flex items-center justify-center px-4 py-4 sm:py-8 sm:px-8 xl:py-12 2xl:py-16">
        <div className="w-full max-w-[1100px] max-h-[calc(100svh-2rem)] sm:max-h-[calc(100svh-4rem)] xl:max-h-[calc(100svh-6rem)] 2xl:max-h-[calc(100svh-8rem)] flex flex-col overflow-hidden rounded-xl shadow-3 bg-white relative modal-content">
          <div className="flex items-center justify-between p-4 sm:p-7.5 border-b border-gray-3 shrink-0">
            <h2 className="text-xl sm:text-2xl font-bold text-dark">
              {t("title", { productName })}
            </h2>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 duration-150 ease-in rounded-full bg-meta text-body max-lg:text-dark lg:hover:text-dark"
            >
              <span className="sr-only">{t("closeModalAria")}</span>
              <XIcon />
            </button>
          </div>

          <div
            className="flex-1 overflow-y-auto p-4 sm:p-7.5 bg-gray-1"
            style={{
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              willChange: "scroll-position",
              overflowX: "hidden",
            }}
          >
            {!hasProductContext ? (
              <div className="flex items-center justify-center h-[220px]">
                <p className="text-body text-base">
                  {t("missingProductContext")}
                </p>
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center h-[220px]">
                <p className="text-body text-base">{t("loading")}</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-[220px] gap-3">
                <p className="text-red text-base">{error}</p>
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded-full bg-blue px-5 py-2 text-sm font-medium text-white hover:bg-blue-dark"
                >
                  {t("retry")}
                </button>
              </div>
            ) : totalReviews > 0 ? (
              <div className="max-w-3xl mx-auto w-full space-y-5 sm:space-y-6">
                <section className="rounded-xl border border-gray-3 bg-white p-5 sm:p-6">
                  <div className="grid gap-5 sm:grid-cols-[220px_1fr] sm:items-center">
                    <div className="px-2 py-1 text-center">
                      <div className="text-4xl font-bold text-dark mb-2">
                        {stats.averageRating.toFixed(1)}
                      </div>
                      <div className="flex items-center justify-center gap-1.5 mb-2">
                        {[...Array(5)].map((_, index) => (
                          <StarIcon
                            key={index}
                            className={`w-6 h-6 ${
                              index < Math.round(stats.averageRating)
                                ? "fill-[#FFA645]"
                                : "fill-gray-4"
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-sm font-medium text-body">
                        {t("reviewsCount", { count: totalReviews })}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      {ratingRows.map((row) => (
                        <div
                          key={row.rating}
                          className="grid grid-cols-[40px_1fr_40px] items-center gap-3"
                        >
                          <div className="flex items-center gap-1 text-xs font-medium text-dark">
                            <span>{row.rating}</span>
                            <StarIcon className="w-3 h-3 fill-[#FFA645]" />
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-gray-3">
                            <div
                              className="h-full rounded-full bg-[#FFA645] transition-all duration-500"
                              style={{ width: `${row.widthPercent}%` }}
                            />
                          </div>
                          <span className="w-10 text-xs font-medium text-body text-right tabular-nums justify-self-end">
                            {row.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                <section className="rounded-xl border border-gray-3 bg-white p-4 sm:p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-dark">
                        {t("sectionTitle")}
                      </h3>
                      <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-gray-3 px-2 text-xs font-semibold text-body tabular-nums">
                        {totalReviews}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="review-sort"
                        className="text-sm font-medium text-body whitespace-nowrap"
                      >
                        {t("sortLabel")}
                      </label>
                      <select
                        id="review-sort"
                        value={sort}
                        onChange={(event) =>
                          onSortChange(event.target.value as ProductReviewSort)
                        }
                        className="rounded-lg border border-gray-3 bg-white px-3 py-2 text-sm text-dark outline-none focus:border-blue"
                      >
                        <option value="newest">{t("sortNewest")}</option>
                        <option value="highest">{t("sortHighest")}</option>
                        <option value="lowest">{t("sortLowest")}</option>
                        <option value="oldest">{t("sortOldest")}</option>
                      </select>
                    </div>
                  </div>
                </section>

                <div className="space-y-4">
                  {reviews.map((review) => (
                    <article
                      key={review.id}
                      className="rounded-xl border border-gray-3 bg-white p-5 sm:p-6"
                    >
                      <div className="mb-3 flex items-start justify-between gap-4">
                        <div>
                          <span className="text-base font-semibold text-dark block">
                            {review.authorName || t("anonymousAuthor")}
                          </span>
                          <span className="text-sm font-medium text-gray-5 mt-1 block">
                            {formatRelativeDate(review.createdAt)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {[...Array(5)].map((_, index) => (
                            <StarIcon
                              key={index}
                              className={`w-4 h-4 ${
                                index < Math.round(review.rating)
                                  ? "fill-[#FFA645]"
                                  : "fill-gray-4"
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      {review.title ? (
                        <p className="mb-2 text-base font-semibold text-dark break-words">
                          {review.title}
                        </p>
                      ) : null}
                      {review.content ? (
                        <p className="text-base leading-relaxed text-body break-words">
                          {review.content}
                        </p>
                      ) : null}
                    </article>
                  ))}
                </div>

                {totalReviews > pageSize ? (
                  <div className="pt-1 pb-4">
                    <Pagination
                      currentPage={page}
                      pageSize={pageSize}
                      totalCount={total}
                      onPageChange={onPageChange}
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <p className="text-body text-lg">{t("noReviews")}</p>
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default ReviewsModal;
